// src/pages/Checkout.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ShoppingBag, MapPin, CreditCard, Minus, Plus, Trash2,
  Percent, Check, Package, Truck, AlertCircle, X
} from 'lucide-react';
import {
  cartAPI, ordersAPI, authAPI, promosAPI, deliveryAPI, productsAPI
} from '../services/api';
import { track, trackOnce } from '../utils/metaPixel';
import './css/checkout.css';

// Safe option label – handles string/number/null
const formatOptionLabel = (group, value) => {
  const extraPrice = Number(value.extra_price) || 0;
  const extra = extraPrice > 0 ? ` (+DT ${extraPrice.toFixed(2)})` : '';
  return `${group.name}: ${value.value}${extra}`;
};

const Checkout = () => {
  const [enrichedItems, setEnrichedItems] = useState([]);
  const [deliverySettings, setDeliverySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    shippingAddress: ''
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState({ discount: 0, valid: false, message: '' });
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize
  useEffect(() => {
    const init = async () => {
      try {
        const token = localStorage.getItem('token');
        setIsAuthenticated(!!token);

        const [cartRes, settingsRes, profileRes] = await Promise.all([
          cartAPI.getCart(),
          deliveryAPI.getSettings(),
          token ? authAPI.getProfile() : Promise.resolve({ data: {} })
        ]);

        const items = Array.isArray(cartRes.data) ? cartRes.data : [];
        const settings = settingsRes.data || { is_enabled: false, delivery_fee: 0, min_free_delivery: 0 };
        setDeliverySettings(settings);

        if (token && profileRes.data) {
          setUserData({
            firstName: profileRes.data.firstName || '',
            lastName: profileRes.data.lastName || '',
            phoneNumber: profileRes.data.phoneNumber || '',
            shippingAddress: profileRes.data.shippingAddress || ''
          });
        }

        if (items.length === 0) {
          setLoading(false);
          return;
        }

        const enriched = await enrichCartItems(items);
        setEnrichedItems(enriched);
      } catch (err) {
        console.error('Checkout init error:', err);
        setError('Impossible de charger le paiement. Veuillez reessayer.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  // Enrich items safely
  const enrichCartItems = async (items) => {
    const productIds = [...new Set(items.map(i => i.productId))];
    const responses = await Promise.allSettled(
      productIds.map(id => productsAPI.getById(id))
    );

    const productMap = {};
    responses.forEach((res, i) => {
      if (res.status === 'fulfilled') {
        productMap[productIds[i]] = res.value.data;
      }
    });

    return items.map(item => {
      const product = productMap[item.productId];
      if (!product) {
        return { ...item, name: 'Produit introuvable', imageUrl: null, finalPrice: 0 };
      }

      const basePrice = Number(product.salePrice || product.regularPrice || 0);
      let optionExtra = 0;
      const optionLabels = [];

      if (product.options && item.selected_options) {
        product.options.forEach(group => {
          const valueId = item.selected_options[group.id];
          if (valueId) {
            const value = group.values?.find(v => v.id === Number(valueId));
            if (value) {
              const extra = Number(value.extra_price) || 0;
              optionExtra += extra;
              optionLabels.push({
                groupName: group.name,
                valueName: value.value,
                extraPrice: extra
              });
            }
          }
        });
      }

      const finalPrice = basePrice + optionExtra;

      return {
        ...item,
        name: product.name,
        imageUrl: product.imageUrl || item.imageUrl,
        basePrice,
        optionExtra,
        finalPrice,
        optionLabels,
        hasOptions: !!product.options?.length
      };
    });
  };

  // Update quantity
  const updateQuantity = async (productId, newQty) => {
    if (newQty < 1) return;

    try {
      const product = await productsAPI.getById(productId);
      const stock = Number(product.data.stock) || 0;

      if (newQty > stock) {
        setError(`Seulement ${stock} en stock`);
        return;
      }

      await cartAPI.updateQuantity({ productId, quantity: newQty });
      setEnrichedItems(prev =>
        prev.map(item =>
          item.productId === productId ? { ...item, quantity: newQty } : item
        )
      );
      setError('');
    } catch (err) {
      setError('Echec de mise a jour de la quantite');
    }
  };

  // Remove item
  const removeItem = async (productId) => {
    try {
      await cartAPI.deleteItem(productId);
      setEnrichedItems(prev => prev.filter(i => i.productId !== productId));
      setError('');
    } catch (err) {
      setError("Echec de la suppression de l'article");
    }
  };

  // Apply promo
  const applyPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;

    try {
      const response = await promosAPI.apply({
        code: code.toUpperCase(),
        items: enrichedItems.map(i => ({
          productId: i.productId,
          quantity: i.quantity,
          price: i.finalPrice,
          selected_options: i.selected_options || {}
        })),
        subtotal: subtotal
      });

      const { discount, message } = response.data;
      setPromoResult({ discount, valid: true, message: message || 'Promo appliquee !' });
      setError('');
    } catch (err) {
      const msg = err.response?.data?.message || 'Code promo invalide';
      setPromoResult({ discount: 0, valid: false, message: msg });
      setError(msg);
    }
  };

  // Remove promo
  const removePromo = () => {
    setPromoCode('');
    setPromoResult({ discount: 0, valid: false, message: '' });
    setError('');
  };

  // Calculations
  const subtotal = useMemo(() =>
    enrichedItems.reduce((sum, item) => sum + (item.finalPrice * item.quantity), 0),
    [enrichedItems]
  );

  const postDiscountSubtotal = Math.max(0, subtotal - promoResult.discount);
  const minFreeDelivery = Number(deliverySettings?.min_free_delivery) || 0;
  const deliveryEnabled = deliverySettings?.is_enabled === true;
  const freeDeliveryThresholdMet = deliveryEnabled && minFreeDelivery > 0 && postDiscountSubtotal >= minFreeDelivery;
  const deliveryFee = deliveryEnabled && !freeDeliveryThresholdMet
    ? Number(deliverySettings?.delivery_fee) || 0
    : 0;
  const total = Math.max(0, postDiscountSubtotal + deliveryFee);
  const progress = minFreeDelivery > 0 ? (postDiscountSubtotal / minFreeDelivery) * 100 : 0;

  useEffect(() => {
    if (loading || enrichedItems.length === 0) return;

    const contentIds = enrichedItems.map(item => String(item.productId));
    const numItems = enrichedItems.reduce((sum, item) => sum + item.quantity, 0);

    trackOnce(
      'InitiateCheckout',
      {
        content_ids: contentIds,
        content_type: 'product',
        num_items: numItems,
        value: total,
        currency: 'TND'
      },
      `initiate-checkout:${location.key}`
    );
  }, [loading, enrichedItems, total, location.key]);

  // Place order
  const handlePlaceOrder = async () => {
    if (!userData.firstName?.trim() || !userData.lastName?.trim() || !userData.phoneNumber?.trim() || !userData.shippingAddress?.trim()) {
      setError('Veuillez remplir toutes les informations de livraison');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const orderData = {
        items: enrichedItems.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          price: item.finalPrice,
          selected_options: item.selected_options || {}
        })),
        firstName: userData.firstName.trim(),
        lastName: userData.lastName.trim(),
        phoneNumber: userData.phoneNumber.trim(),
        shippingAddress: userData.shippingAddress.trim(),
      };

      if (promoResult.valid && promoCode.trim()) {
        orderData.promo_code = promoCode.trim().toUpperCase();
      }

      const res = await ordersAPI.placeOrder(orderData);
      if (res.status === 201 && res.data?.orderId) {
        track('Purchase', {
          content_ids: orderData.items.map(item => String(item.productId)),
          content_type: 'product',
          value: Number(res.data.total || 0),
          currency: 'TND',
          order_id: String(res.data.orderId)
        });
      }
      await cartAPI.clearCart();


      const orderId = res.data?.id || res.data?.order?.id || res.data?.data?.id;
      const trackingCode = res.data?.trackingCode || res.data?.order?.trackingCode || res.data?.data?.trackingCode;

      if (isAuthenticated) {
        if (!orderId) {
          setError('Commande placee avec succes ! Consultez votre profil pour les details.');
          navigate('/profile');
          return;
        }
        navigate(`/order-success/auth/${orderId}`);
      } else {
        if (!trackingCode) {
          setError('Commande placee avec succes ! Un email de confirmation a ete envoye.');
          navigate('/');
          return;
        }
        navigate(`/order-success/guest/${trackingCode}`);
      }
    } catch (err) {
      console.error('Order placement error:', err);
      const msg = err.response?.data?.message || 'Echec de la commande. Veuillez reessayer.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="checkout-loading">
        <div className="checkout-spinner"></div>
        <p>Chargement de votre panier...</p>
      </div>
    );
  }

  // Empty cart
  if (enrichedItems.length === 0) {
    return (
      <div className="checkout-empty">
        <Package size={64} />
        <h2>Votre panier est vide</h2>
        <button onClick={() => navigate('/')} className="checkout-continue-btn">
          Continuer les achats
        </button>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div className="checkout-header">
        <h1>Paiement</h1>
        <p>Verifiez votre commande et finalisez votre achat</p>
      </div>

      <div className="checkout-grid">
        {/* Left Column */}
        <div className="checkout-left">

          {/* Items */}
          <div className="checkout-card">
            <div className="checkout-card-header">
              <ShoppingBag size={20} />
              <h2>Vos articles ({enrichedItems.length})</h2>
            </div>

            <div className="checkout-items-list">
              {enrichedItems.map(item => {
                const itemTotal = item.finalPrice * item.quantity;
                return (
                  <div key={`${item.productId}-${JSON.stringify(item.selected_options)}`} className="checkout-item">
                    <div className="checkout-item-image">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="checkout-image-placeholder">
                          <Package size={32} />
                        </div>
                      )}
                    </div>

                    <div className="checkout-item-details">
                      <div className="checkout-item-header">
                        <h4 className="checkout-item-name">{item.name}</h4>
                        <button
                          onClick={() => removeItem(item.productId)}
                          className="checkout-remove-btn"
                          aria-label="Supprimer l'article"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {item.optionLabels?.length > 0 && (
                        <div className="checkout-item-options">
                          {item.optionLabels.map((option, i) => (
                            <div key={i} className="checkout-option-item">
                              <span className="checkout-option-label">{option.groupName}</span>
                              <span className="checkout-option-separator">:</span>
                              <span className="checkout-option-value">{option.valueName}</span>
                              {option.extraPrice > 0 && (
                                <span className="checkout-option-price">+DT {option.extraPrice.toFixed(2)}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="checkout-item-footer">
                        <div className="checkout-quantity-control">
                          <button
                            onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            aria-label="Diminuer la quantite"
                          >
                            <Minus size={16} />
                          </button>
                          <span>{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                            aria-label="Augmenter la quantite"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                        <div className="checkout-item-pricing">
                          <span className="checkout-item-unit-price">DT {item.finalPrice.toFixed(2)} l'unite</span>
                          <span className="checkout-item-total">DT {itemTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Promo */}
          <div className="checkout-card">
            <div className="checkout-card-header">
              <Percent size={18} />
              <h3>Code promo</h3>
            </div>
            <div className="checkout-promo-input-group">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Entrez le code promo"
                disabled={promoResult.valid}
                className="checkout-promo-input"
              />
              {promoResult.valid ? (
                <button
                  onClick={removePromo}
                  className="checkout-promo-btn checkout-promo-remove-btn"
                >
                  <X size={16} />
                  Retirer
                </button>
              ) : (
                <button
                  onClick={applyPromo}
                  disabled={!promoCode.trim()}
                  className="checkout-promo-btn"
                >
                  Appliquer
                </button>
              )}
            </div>
            {promoResult.message && (
              <div className={`checkout-promo-message ${promoResult.valid ? 'success' : 'error'}`}>
                {promoResult.valid ? <Check size={16} /> : <AlertCircle size={16} />}
                <span>{promoResult.message}</span>
              </div>
            )}
          </div>

          {/* Shipping */}
          <div className="checkout-card">
            <div className="checkout-card-header">
              <MapPin size={20} />
              <h2>Informations de livraison</h2>
            </div>

            <div className="checkout-form">
              <div className="checkout-form-row">
                <div className="checkout-form-group">
                  <label>Prenom *</label>
                  <input
                    type="text"
                    value={userData.firstName}
                    onChange={(e) => setUserData(prev => ({ ...prev, firstName: e.target.value }))}
                    disabled={isAuthenticated}
                    required
                  />
                </div>
                <div className="checkout-form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    value={userData.lastName}
                    onChange={(e) => setUserData(prev => ({ ...prev, lastName: e.target.value }))}
                    disabled={isAuthenticated}
                    required
                  />
                </div>
              </div>

              <div className="checkout-form-group">
                <label>Numero de telephone *</label>
                <input
                  type="tel"
                  value={userData.phoneNumber}
                  onChange={(e) => setUserData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  disabled={isAuthenticated}
                  required
                />
              </div>

              <div className="checkout-form-group">
                <label>Adresse de livraison *</label>
                <textarea
                  value={userData.shippingAddress}
                  onChange={(e) => setUserData(prev => ({ ...prev, shippingAddress: e.target.value }))}
                  disabled={isAuthenticated}
                  rows={3}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="checkout-right">
          <div className="checkout-card checkout-summary">
            <h3>Resume de commande</h3>

            <div className="checkout-summary-row">
              <span>Sous-total</span>
              <span>DT {subtotal.toFixed(2)}</span>
            </div>

            {promoResult.discount > 0 && (
              <div className="checkout-summary-row discount">
                <span>Remise</span>
                <span>-DT {promoResult.discount.toFixed(2)}</span>
              </div>
            )}

            {deliveryEnabled && (
              <div className="checkout-summary-row">
              <span>Livraison</span>
                <span>
                  {deliveryFee === 0 ? (
                    <span className="free">Gratuite</span>
                  ) : (
                    `DT ${deliveryFee.toFixed(2)}`
                  )}
                </span>
              </div>
            )}

            <div className="checkout-summary-divider" />

            <div className="checkout-summary-row total">
              <span>Total</span>
              <span className="amount">DT {total.toFixed(2)}</span>
            </div>

            {deliveryEnabled && minFreeDelivery > 0 && postDiscountSubtotal < minFreeDelivery && (
              <div className="checkout-delivery-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${Math.min(progress, 100)}%` }} />
                </div>
                <p>
                  Ajoutez <strong>DT {(minFreeDelivery - postDiscountSubtotal).toFixed(2)}</strong> pour la livraison gratuite
                </p>
              </div>
            )}

            {freeDeliveryThresholdMet && (
              <div className="checkout-free-delivery">
                <Truck size={16} />
                <span>Livraison gratuite appliquee !</span>
              </div>
            )}

            {error && (
              <div className="checkout-error">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <button
              onClick={handlePlaceOrder}
              disabled={submitting}
              className="checkout-place-order-btn"
            >
              {submitting ? 'Traitement...' : (
                <>
                  <CreditCard size={20} />
                  Passer la commande
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
